import { MutationType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { BaseMutation } from '../../base/base-mutation';
import { Rule } from '../../base/mutation-rules';
import { DataType } from '../../../data';
import { isValidTableRange } from '../../../utils/table-utils';
import type { PresentationDataModel } from '../../../model/presentation-data-model';
import type { ICTTableCell, ICTTableRow } from '@tencent/slide-sdk-pb-model';
import type { MaybeError } from '../../base/base-mutation';
import type { IValidSetTableCellPropertiesMutationData } from '../../base/valid-mutation-data';

export class SetTableCellPropertiesMutation extends BaseMutation<
    PresentationDataModel,
    IValidSetTableCellPropertiesMutationData
> {
    public readonly type: MutationType = MutationType.SetTableCellProperties;

    public apply(presentation: PresentationDataModel) {
        if (!this.data.shapeId) return;

        const { shapeId, range, tableCellProperties } = this.data;
        const table = presentation.store.models.get(shapeId, DataType.GraphicalObjectFrameTable);
        assert(table, 'table should exist');
        if (!table) return;
        table.store.setTableCellProperties(range, tableCellProperties);
    }

    public invert(presentation: PresentationDataModel): IValidSetTableCellPropertiesMutationData[] {
        const { pageId, shapeId, range } = this.data;
        const table = presentation.store.models.get(shapeId, DataType.GraphicalObjectFrameTable);
        assert(table, 'table is null');
        assert(isValidTableRange(range), 'range is invalid');

        const { startRowIndex, rowSpan, startColIndex, colSpan } = range;
        const subTable = table.store.tableData?.tr
            ?.slice(startRowIndex, startRowIndex + rowSpan)
            .map((row: ICTTableRow) => ({
                h: row.h,
                tc: (row?.tc && row.tc.slice(startColIndex, startColIndex + colSpan)) || [],
            }));

        const muts: IValidSetTableCellPropertiesMutationData[] = [];
        subTable?.forEach((row: ICTTableRow, rowIndex) => {
            row?.tc?.forEach((cell: ICTTableCell, colIndex) => {
                muts.push({
                    type: MutationType.SetTableCellProperties,
                    pageId,
                    shapeId,
                    range: {
                        startRowIndex: startRowIndex + rowIndex,
                        startColIndex: startColIndex + colIndex,
                        rowSpan: 1,
                        colSpan: 1,
                    },
                    tableCellProperties: cell?.tcPr ? cell.tcPr : null, // tcPr 全量替换，invert 直接替代即可
                });
            });
        });
        return muts;
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageId, shapeId, range } = this.data;
        return Rule.every(
            Rule.shape.should.existInPage(presentation, shapeId, pageId),
            Rule.table.can.setTableCells(presentation, shapeId, range),
        );
    }
}
